import {
  GUITAR_GEM_HEIGHT_FRAC,
  GUITAR_STRUM_BAR_Y_FRAC,
} from '@shared/guitarChart';
import type { ManiaLane } from '@shared/maniaNote';

/**
 * Centralised tuning + layout constants for the Guitar render
 * subsystem. Owning these in one file keeps the magic numbers
 * searchable and lets one change (e.g. fret colour, perspective
 * angle) ripple to every draw module without hunting through five
 * files.
 *
 * The two values that the engine cares about
 * (`STRUM_BAR_Y_FRAC` + `GEM_HEIGHT_FRAC`) are re-exported from
 * `guitarChart.ts` so the engine's geometric perfect window stays in
 * lockstep with the renderer's visual.
 */

export const STRUM_BAR_Y_FRAC = GUITAR_STRUM_BAR_Y_FRAC;
export const GEM_HEIGHT_FRAC = GUITAR_GEM_HEIGHT_FRAC;

/**
 * Maximum CSS width of the playing field (lane stack at the strum bar
 * + horizontal padding). On wider canvases the field is centred
 * horizontally and the leftover space becomes a dark margin. Slightly
 * wider than Piano's 480 to give the perspective trapezoid a more
 * "stage-sized" feel when the lanes diverge.
 */
export const PLAYING_FIELD_MAX_WIDTH_PX = 540;

/**
 * Padding either side of the lane stack within the playing field
 * (measured at the strum bar). Keeps the HUD chrome (HP, score,
 * combo) off the highway and away from the gem-tap zone.
 */
export const LANE_STACK_HORIZONTAL_PAD_PX = 24;

/**
 * Visual gap between adjacent lane gutters at the strum bar. Tight
 * because the perspective trapezoid already gives every lane a clear
 * silhouette as it diverges away from the centre.
 */
export const LANE_GAP_PX = 4;

/**
 * Strength of the perspective foreshortening at the highway's far
 * end (top of the screen). The lane stack at the top is
 * `bottomWidth × PERSPECTIVE_TOP_SCALE`, so 0.32 gives a roughly
 * Guitar-Hero-ish trapezoid where the far lanes converge to about a
 * third the width of the foreground lanes.
 */
export const PERSPECTIVE_TOP_SCALE = 0.32;

/**
 * Y coordinate (as fraction of canvas height) where the highway
 * starts emerging from the back wall. Anything above this y is the
 * background gradient — gems spawn at this y and travel down to the
 * strum bar over `approachMs` wall-clock time.
 */
export const HIGHWAY_TOP_Y_FRAC = 0.04;

/**
 * Gem corner radius in CSS px before the perspective scale. Scaled
 * down for far gems to keep the silhouette consistent with the
 * receding lane width.
 */
export const GEM_CORNER_RADIUS_PX = 10;

/**
 * Hard cap on long-lived particles. Sized smaller than Piano's 600
 * because Guitar's denser charts spawn FX more often per second —
 * keeping the cap modest stops a sustained perfect stream from
 * pinning the GPU on phones.
 */
export const PARTICLE_HARD_CAP = 480;

/**
 * Hit-stop micro-freeze cap (ms). Same as Piano so a sustained
 * perfect stream can't permanently freeze the renderer on either
 * mode.
 */
export const HIT_STOP_MAX_MS = 80;

/**
 * Pale-gold accent for the second sparkle layer fired on perfect
 * hits in Guitar. Sits visually above the GH-classic fret colours so
 * the eye reads "energy released" rather than blending into the
 * lane's primary hue.
 */
export const PERFECT_SPARKLE_COLOUR = '#fff4b3';

/**
 * Combo at which the background bloom + strum-bar glow start cycling
 * through combo-tier hues. Lower than Piano's 10 so Guitar players
 * who maintain a streak see the playfield "wake up" sooner — fits
 * the higher-density mode where short combos are common but long
 * combos are the prestige cue.
 */
export const COMBO_BG_TIER_THRESHOLD = 8;

/**
 * Combo-tier hue cycle for the strum-bar bloom + bg accent. Hues are
 * deliberately well-separated so the eye reads each tier crossover
 * as a distinct "the music levelled up" cue. Distinct from Piano's
 * mint/cyan vocabulary — Guitar leans warm-orange / neon-pink to
 * read as a separate mode at a glance.
 */
export const COMBO_BLOOM_PALETTE = ['#ff7a3b', '#ffb83b', '#3bff9d', '#ff2bd6'] as const;

/**
 * Per-lane base colours for the gem heads (and the lane fret cap at
 * the strum bar). Mirrors the GH-classic palette: green / red /
 * yellow / blue. The colours are saturated enough to read as
 * unambiguous lane identifiers even when the highway is dim, and
 * pair cleanly with the Guitar palette's `bg` / `bgAccent` purples.
 */
export const LANE_GEM_COLOURS: Record<ManiaLane, string> = {
  0: '#3bff7a',
  1: '#ff3b5c',
  2: '#ffd84d',
  3: '#3b9dff',
};

/**
 * Lighter accent matching each lane gem colour, used for hold-ribbon
 * highlights + active-press glows.
 */
export const LANE_GEM_ACCENTS: Record<ManiaLane, string> = {
  0: '#aaffc7',
  1: '#ffb6c1',
  2: '#fff0a3',
  3: '#a3d8ff',
};

/**
 * Default key labels rendered above each lane's strum-bar fret cap.
 * These are the static defaults — actual keybinds may differ if the
 * player rebound them, but the labels read the live prefs at draw
 * time so the right physical key is shown.
 */
export const DEFAULT_KEY_LABELS: Record<ManiaLane, string> = {
  0: 'D',
  1: 'F',
  2: 'J',
  3: 'K',
};

export const HP_BAR_HEIGHT_PX = 8;
export const HP_BAR_TOP_PX = 16;

/**
 * HP fraction below which the bar pulses its red fill via a glow
 * shadow. Same threshold semantics as Piano (see
 * `pianoRenderConstants.HP_DANGER_PULSE_THRESHOLD`).
 */
export const HP_DANGER_PULSE_THRESHOLD = 0.3;

/**
 * Number of segments to divide the HP bar into. Four segments draws
 * three internal tick marks at 25 / 50 / 75 % — quartile milestones
 * the player can glance at without parsing raw width. Reached ticks
 * brighten so the bar reads as a "filling battery."
 */
export const HP_TICK_DIVISIONS = 4;
export const HP_TICK_THICKNESS_PX = 1.5;

/** Fraction of `perfectOverlapBeats` for the late-tail danger pulse. Same as Piano. */
export const DANGER_WINDOW_FRAC = 0.5;

/** Approximate y (in CSS px) at which to anchor score-pop flyer targets. Mirrors Piano. */
export const SCORE_HUD_TARGET_TOP_PAD_PX = 56;

/** Combo HUD base font size in CSS px before scale / pulse modifiers. */
export const COMBO_HUD_BASE_FONT_PX = 28;

/** Minimum combo at which the under-text streak underline appears. */
export const COMBO_UNDERLINE_MIN = 5;

/**
 * Number of scrolling speed-line stripes drawn per lane. The stripes
 * drift from the back wall toward the strum bar so the eye always
 * reads forward motion, even between gems on a sparse chart.
 */
export const SPEED_LINE_COUNT_PER_LANE = 4;

/** Seconds for a single stripe to traverse the highway top-to-bottom. */
export const SPEED_LINE_TRAVEL_S = 1.6;

/** Stripe thickness at the foreground (strum bar) end in CSS px. */
export const SPEED_LINE_THICKNESS_PX = 3;

/** Peak alpha of a speed-line stripe at the strum-bar end of the trapezoid. */
export const SPEED_LINE_ALPHA = 0.22;

/**
 * Strength of the per-beat pulse applied to strum-bar glow + fret-cap
 * radius. The pulse decays as `(1 - phase)^p` so the peak hits right
 * on the beat boundary and fades through the bar — feels like a
 * stadium kick drum thump rather than a steady oscillation.
 */
export const BEAT_PULSE_STRENGTH = 0.32;

/** Exponent on the beat-phase decay (higher = tighter thump). */
export const BEAT_PULSE_DECAY_POWER = 2.6;

/**
 * Radial-glow brightness at the highway's far horizon. Combines with
 * the combo tier so the horizon "wakes up" as the player climbs.
 */
export const HORIZON_GLOW_BASE_ALPHA = 0.22;
export const HORIZON_GLOW_TIER_BOOST = 0.09;
export const HORIZON_GLOW_RADIUS_FRAC = 0.55;

/** Vignette darkness at the canvas corners (alpha at outermost edge). */
export const VIGNETTE_ALPHA = 0.45;

/**
 * Forward gem motion trail — a faint, smaller copy of the gem placed
 * `OFFSET_PROGRESS` behind it along the lane. Stacks two passes for a
 * smoother streak. Pure visual; no allocation per frame.
 */
export const GEM_TRAIL_OFFSET_PROGRESS = 0.05;
export const GEM_TRAIL_ALPHA = 0.42;

/**
 * "Lock-in" pulse applied to a gem entering its final stretch toward
 * the strum bar — a one-way ramp from 0 at `APPROACH_PULSE_START` to
 * 1 at `APPROACH_PULSE_END` (no decay). The pulse scales the gem
 * slightly larger and bumps its alpha, peaking right as it hits the
 * strum bar so the player gets a "tap now" telegraph that arrives
 * with the gem rather than ahead of it.
 *
 * Both bounds are in perspective-projected screen progress (0 = far
 * horizon, 1 = strum bar), matching the coordinate space the
 * renderer projects gems through via {@link perspectiveProgress}. A
 * start value of 0.5 means the pulse begins when the gem has crossed
 * half the visible highway depth — late enough that early-travel
 * gems don't pulse, but with enough runway for the ramp to read as
 * "growing" rather than a snap. Earlier versions had a peak-then-
 * decay shape that read as the gem "slowing down" right before the
 * strum bar; Guitar Hero's iconic feel has no such deceleration.
 */
export const APPROACH_PULSE_START = 0.5;
export const APPROACH_PULSE_END = 1.0;
export const APPROACH_PULSE_STRENGTH = 0.18;

/**
 * Animated chevron stream rendered on top of an engaged hold ribbon.
 * Six chevrons cycle continuously, each phase-offset by 1/N, so the
 * player sees "energy flowing" along the sustain.
 */
export const HOLD_CHEVRON_COUNT = 6;
export const HOLD_CHEVRON_CYCLE_S = 0.85;
export const HOLD_CHEVRON_ALPHA = 0.55;

/**
 * Per-lane upward chevron stream rendered when a lane is currently
 * pressed but has no gem actively crossing the strum bar. Reads as
 * "the lane is armed and listening" — distinct from the highway's
 * pressed-lane wash and the active-hold chevrons (those flow toward
 * the player; these flow toward the horizon). Subtle alpha so the
 * approaching gems remain the primary read.
 */
export const PRESS_CHEVRON_COUNT = 3;
export const PRESS_CHEVRON_CYCLE_S = 0.7;
export const PRESS_CHEVRON_ALPHA = 0.32;

/**
 * Concentric ring shockwaves spawned at each countdown integer. Each
 * ring expands from `START_R` to `END_R` over `LIFE_S`, fading as it
 * grows. Three rings per integer so the lead-in feels like a stadium
 * cue rather than a sterile number flick.
 */
export const COUNTDOWN_RING_COUNT = 3;
export const COUNTDOWN_RING_LIFE_S = 0.55;
export const COUNTDOWN_RING_START_R = 30;
export const COUNTDOWN_RING_END_R = 220;

/**
 * Lightning bolt FX — jagged arcs across the strum bar when two or
 * more lane flashes arrive simultaneously (chord hit). Pool-backed to
 * keep per-frame allocations at zero.
 */
export const LIGHTNING_BOLT_LIFE_S = 0.32;
export const LIGHTNING_BOLT_THICKNESS_PX = 2.4;
export const LIGHTNING_JAG_SEGMENTS = 7;
export const LIGHTNING_JAG_AMPLITUDE_PX = 14;
export const LIGHTNING_POOL_SIZE = 8;
export const LIGHTNING_COOLDOWN_S = 0.1;
export const LIGHTNING_COLOUR = '#fff4b3';
export const LIGHTNING_COMBO_THRESHOLD = 3;
